import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Headphones, Loader2, Rocket } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "/images/logo-full.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialUpgrade() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (user?.account_status === "trial_expired") {
      navigate("/trial-expired", { replace: true });
    }
  }, [user?.account_status, navigate]);

  const handleUpgrade = async () => {
    if (!token) {
      window.location.href = "/register";
      return;
    }
    setUpgrading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/billing/trial/upgrade-checkout`,
        { plan: "monthly", plan_tier: user?.plan_tier || "tier_1" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        toast.error("Impossibile avviare il checkout. Riprova o contatta l'assistenza.");
      }
    } catch (error) {
      console.error("Upgrade checkout error:", error);
      toast.error(error.response?.data?.detail || "Errore durante l'avvio dell'upgrade");
    } finally {
      setUpgrading(false);
    }
  };

  const expiryLabel = user?.trial_expires_at
    ? new Date(user.trial_expires_at).toLocaleDateString("it-IT", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <div className="min-h-screen bg-[#f8fbfa] flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg text-center">
          <img src={LOGO_URL} alt="Affidya" className="mx-auto mb-8 h-12" />

          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
            <Rocket className="h-10 w-10 text-emerald-600" />
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Passa al piano completo
          </h1>
          <p className="mt-4 text-sm leading-6 text-slate-600">
            Attiva un abbonamento per continuare ad usare Affidya senza interruzioni.
            Tutti i dati creati durante la prova gratuita saranno mantenuti.
          </p>
          {expiryLabel && (
            <p className="mt-3 text-sm font-medium text-emerald-700">
              Prova gratuita attiva fino al {expiryLabel}
            </p>
          )}

          <div className="mt-8 space-y-3">
            <Button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
            >
              {upgrading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Reindirizzamento...
                </span>
              ) : (
                "Effettua upgrade"
              )}
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-12 w-full rounded-xl border-slate-200 text-base font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              <a href="mailto:supporto@affidya.it?subject=Upgrade%20Affidya">
                <Headphones className="mr-2 h-5 w-5" />
                Contatta assistenza
              </a>
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate("/")}
              className="h-11 w-full text-sm text-slate-600 hover:text-slate-900"
            >
              Torna alla dashboard
            </Button>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center">
        <div className="flex justify-center gap-5 text-xs text-slate-500">
          <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-slate-700">Cookie Policy</Link>
          <Link to="/terms" className="hover:text-slate-700">Termini di Servizio</Link>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          © {new Date().getFullYear()} Affidya S.r.l.
        </p>
      </footer>
    </div>
  );
}
